import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, catchError, finalize, of, skip, Subject, Subscription, switchMap, take, tap } from 'rxjs';
import { IAddOrgMemberData } from '../../models/organization/members/add-org-member-data.model';
import { IInviteUnregisteredMemberData } from '../../models/organization/members/invite-unregistered-member-data.model';
import { IRemoveOrgMemberData } from '../../models/organization/members/remove-org-member-data.model';
import { IUpdateOrgMemberData } from '../../models/organization/members/update-org-member-data.model';
import { IUser } from '../../models/user/user.model';
import { OrganizationsService } from '../entities/organizations/organizations.service';
import { UsersService } from '../entities/users/users.service';
import { SelectedOrgService } from '../selected-org.service';
import { UtilsService } from '../utils.service';
import { StoreService } from './store.service';
import { RoleType } from '../../enums/user/role-types.enum';
import { ActionBy } from '../../enums/utils/action-by.enum';
import { UserPreferencesStoreService } from './user-preferences-store.service';

export interface IOrgUsersFilterData {
  [uuid: string]: any[] | undefined;
  roles?: RoleType[];
}

export interface IOrgUsersState {
  users$: BehaviorSubject<IUser[]>;
  fetchingUsers$: BehaviorSubject<boolean>;
  firstFetchedUsers$: BehaviorSubject<boolean>;
  updatingUsers$: BehaviorSubject<number[]>;

  orgUsersFilter$: BehaviorSubject<IOrgUsersFilterData>;

  addingMember$: BehaviorSubject<boolean>;
  invitingUnregistered$: BehaviorSubject<number[]>;
};

@Injectable({
  providedIn: 'root'
})
export class OrgUsersStoreService implements OnDestroy {

  private initState: IOrgUsersState = {
    users$: new BehaviorSubject<IUser[]>([]),
    fetchingUsers$: new BehaviorSubject<boolean>(false),
    firstFetchedUsers$: new BehaviorSubject<boolean>(false),
    updatingUsers$: new BehaviorSubject<number[]>([]),
    addingMember$: new BehaviorSubject<boolean>(false),
    invitingUnregistered$: new BehaviorSubject<number[]>([]),
    orgUsersFilter$: new BehaviorSubject<IOrgUsersFilterData>({})
  };
  private state: IOrgUsersState = this.utilsService.initializeState(this.initState) as IOrgUsersState;

  private inited = false;
  private subs: Subscription[] = [];

  private initOrgUsersTrigger$ = new Subject<void>(); // trigger pro init org users

  constructor(
    private selectedOrgService: SelectedOrgService,
    private userPreferencesStore: UserPreferencesStoreService,
    private usersService: UsersService,
    private organizationsService: OrganizationsService,
    private utilsService: UtilsService,
    private store: StoreService,
  ) {
    this.subs.push(

      this.userPreferencesStore.state.preferences$.subscribe((prefs) => {
        if (!prefs) return;
        if (prefs.admin_org_users_filter) {
          const filters = Object.entries(prefs.admin_org_users_filter).reduce((prev, [key, value]) => ({
            ...prev,
            [key]: value?.map((x) => x.value)
          }), {});
          this.state.orgUsersFilter$.next(filters);
          this.initOrgUsers();
        }
      }),

      this.initOrgUsersTrigger$.pipe(
        switchMap(() => {
          const orgId = this.selectedOrgService.getCurrentValue();
          if (!orgId) return of([]);

          this.state.fetchingUsers$.next(true);
          return this.usersService.getUsersByOrganizationId({
            actionBy: ActionBy.ADMIN,
            roles: [RoleType.COORDINATOR, RoleType.LECTURER, RoleType.ORG_ADMIN, RoleType.ORG_OWNER, RoleType.SYSTEM_ADMIN],
            organizationId: orgId,
            filters: this.state.orgUsersFilter$.value
          }).pipe(
            take(1),
            finalize(() => {
              this.state.fetchingUsers$.next(false);
              this.state.firstFetchedUsers$.next(true);
            }),
            catchError(err => {
              return of([]);
            })
          );
        })
      ).subscribe({
        next: (res) => {
          this.state.users$.next(res);
        }
      }),

      // actions

      // member added - add it to store
      this.store.actions.orgUsers_memberAdded$.subscribe(user => {
        this.state.users$.next([ ...this.state.users$.getValue(), user ]);
      }),
      //

      // member updated - update/remove from store
      this.store.actions.orgUsers_memberUpdated$.subscribe(data => {
        const currentUsers = this.state.users$.getValue();
        const index = currentUsers.findIndex(x => x.id === data.userId);
        // update
        if (data.user) {
          currentUsers[index] = data.user;
        }
        // remove
        else {
          currentUsers.splice(index, 1);
        }
        this.state.users$.next(currentUsers);
      }),
      //

      // member removed - remove from store
      this.store.actions.orgUsers_memberRemoved$.subscribe(userId => {
        const currUsers = this.state.users$.getValue();
        const index = currUsers.findIndex(x => x.id === userId);
        currUsers.splice(index, 1);
        this.state.users$.next(currUsers);
      }),
      //

      // onremove member
      this.store.actions.orgUsers_removeMember$.subscribe((data) => {
        this.removeOrgMember(data).subscribe();
      }),
      //

      // on invite unregistered
      this.store.actions.orgUsers_inviteUnregistered$.subscribe((data) => {
        this.inviteUnregisteredMember(data).subscribe();
      }),
      //
    );
  }

  public init() {
    this.inited = true;
    this.reset();

    const orgId = this.selectedOrgService.getCurrentValue();
    if (!orgId) return;

    this.initOrgUsers();
  }

  private initOrgUsers() {
    this.initOrgUsersTrigger$.next();
  }

  public addOrgMember(data: IAddOrgMemberData) {
    this.state.addingMember$.next(true);
    return this.organizationsService.addOrgMember(data).pipe(
      take(1),
      finalize(() => this.state.addingMember$.next(false)),
      tap((x) => {
        this.store.actions.orgUsers_memberAdded$.next(x);
      })
    );
  }

  public updateOrgMember(data: IUpdateOrgMemberData) {
    this.state.updatingUsers$.next([ ...this.state.updatingUsers$.getValue(), data.userId ]);
    return this.organizationsService.updateOrgMember(data).pipe(
      take(1),
      finalize(() => this.state.updatingUsers$.next( this.state.updatingUsers$.getValue().filter(x => x !== data.userId)) ),
      tap((x) => {
        this.store.actions.orgUsers_memberUpdated$.next({ userId: data.userId, user: x });
      })
    );
  }

  public removeOrgMember(data: IRemoveOrgMemberData) {
    this.state.updatingUsers$.next([ ...this.state.updatingUsers$.getValue(), data.userId ]);
    return this.organizationsService.removeOrgMember(data).pipe(
      take(1),
      finalize(() => this.state.updatingUsers$.next( this.state.updatingUsers$.getValue().filter(x => x !== data.userId)) ),
      tap(() => {
        this.store.actions.orgUsers_memberRemoved$.next( data.userId );
      })
    );
  }

  public inviteUnregisteredMember(data: IInviteUnregisteredMemberData) {
    this.state.invitingUnregistered$.next([ ...this.state.invitingUnregistered$.getValue(), data.userId ]);
    return this.organizationsService.inviteUnregistered(data).pipe(
      take(1),
      finalize(() => this.state.invitingUnregistered$.next( this.state.invitingUnregistered$.getValue().filter(x => x !== data.userId)) ),

    );
  }

  private reset() {
    this.utilsService.resetState(this.initState, this.state);
  }

  public getState() {
    return this.state;
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }
}
